import { defineComponent, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import { Icon } from '@iconify/vue'
import {
  Button as VanButton,
  CellGroup,
  Field,
  Tab,
  Tabs,
  showFailToast,
  showSuccessToast,
  showToast,
} from 'vant'
import { useAuthStore } from '@/stores/auth-store'
import { useSiteConfigStore } from '@/stores/site-config-store'
import { useCountdown } from '@/composables/use-countdown'
import { useServerCaptcha } from '@/composables/use-server-captcha'
import { postApiV1Login, postApiV1LoginRegister } from '@/api/sdk.gen'
import iconUser from '@/assets/login/icons/user.svg'
import iconShield from '@/assets/login/icons/shield.svg'

// public/ 下的静态文件按站点根路径引用（构建产物里原 import 也只是这个字符串）
const appIcon = '/icon.jpg'

interface LoginRespData {
  token: string
  refresh_token: string
  nickname: string
  user_id?: number
}

export default defineComponent({
  name: 'LoginPage',
  setup() {
    const router = useRouter()
    const goBack = useSmartBack()
    const authStore = useAuthStore()
    const siteConfigStore = useSiteConfigStore()

    const activeTab = ref(1)
    const phone = ref('')
    const captchaInput = ref('')
    const password = ref('')
    const code = ref('')
    const sendLoading = ref(false)
    const loginLoading = ref(false)
    const { countdown, isCounting, start } = useCountdown()
    const {
      captchaImage,
      refresh: refreshCaptcha,
      verify: verifyCaptcha,
      sendSmsCode,
    } = useServerCaptcha()

    function clearOnTabSwitch() {
      captchaInput.value = ''
      code.value = ''
    }

    async function handleSendCode() {
      if (phone.value.length !== 11) return showToast('请输入正确的手机号')
      if (!captchaInput.value) return showToast('请输入图形验证码')
      sendLoading.value = true
      // 发短信前先单独校验图形验证码（消费式，通过后 captcha_id 立即失效）
      await sendSmsCode({
        captchaCode: captchaInput.value,
        account: phone.value,
        scene: 'sms_login',
        failText: '验证码发送失败',
        successText: '验证码已发送',
        onSent: start,
      })
      sendLoading.value = false
    }

    /** 发登录请求：成功存登录态并跳「我的」，失败提示并换一张图形验证码；request 返回 null 表示已提前终止 */
    async function runLogin(request: () => Promise<{ data?: unknown; error?: unknown } | null>) {
      loginLoading.value = true
      try {
        const res = await request()
        if (!res) return
        const body = (res.data ?? res.error) as
          | { code: number; message: string; data: LoginRespData | null }
          | undefined
        if (body?.code !== 0 || !body.data) {
          showFailToast(body?.message || '登录失败')
          refreshCaptcha()
          return
        }
        const data = body.data
        authStore.setAuth(
          data.token,
          { id: data.user_id, username: data.nickname, phone: phone.value },
          data.refresh_token,
        )
        showSuccessToast('登录成功')
        router.replace('/my')
      } catch {
        showFailToast('登录失败')
        refreshCaptcha()
      } finally {
        loginLoading.value = false
      }
    }

    function handlePasswordLogin() {
      if (!phone.value) return showToast('请输入手机号')
      if (!captchaInput.value) return showToast('请输入图形验证码')
      if (!password.value) return showToast('请输入密码')
      return runLogin(async () => {
        if (!(await verifyCaptcha(captchaInput.value))) return null
        return postApiV1Login({
          body: {
            channel: 'phone',
            account: phone.value,
            password: password.value,
          } as never,
        })
      })
    }

    function handleCodeLogin() {
      if (phone.value.length !== 11) return showToast('请输入正确的手机号')
      if (!code.value) return showToast('请输入验证码')
      return runLogin(() =>
        postApiV1LoginRegister({
          body: {
            channel: 'phone',
            account: phone.value,
            code: code.value,
          } as never,
        }),
      )
    }

    function handleSubmit() {
      if (activeTab.value === 1) handlePasswordLogin()
      else handleCodeLogin()
    }

    // ===== 行片段（.tbox > .icons + .tright(.tips + Field)） =====

    const renderPhoneRow = () => (
      <div class="tbox">
        <div class="icons">
          <img src={iconUser} alt="user" />
        </div>
        <div class="tright">
          <div class="tips">用户名</div>
          <Field
            v-model={phone.value}
            maxlength={11}
            placeholder="输入您手机号码"
            formatter={(v: string) => v.replace(/\D/g, '').slice(0, 11)}
          />
        </div>
      </div>
    )

    const renderCaptchaRow = () => (
      <div class="tbox">
        <div class="icons">
          <img src={iconShield} alt="shield" />
        </div>
        <div class="tright">
          <div class="tips">图形验证码</div>
          <Field v-model={captchaInput.value} maxlength={6} placeholder="输入图形验证码">
            {{
              'right-icon': () => (
                <button type="button" class="btn-reset" aria-label="刷新验证码" onClick={refreshCaptcha}>
                  <img src={captchaImage.value} class="h-[0.5rem] cursor-pointer" alt="captcha" />
                </button>
              ),
            }}
          </Field>
        </div>
      </div>
    )

    /** 验证码输入框右侧「获取验证码 / 倒计时」 */
    const renderSendCodeButton = () => (
      <button
        type="button"
        class="get-code btn-reset"
        onClick={() => {
          if (!isCounting.value && !sendLoading.value) handleSendCode()
        }}
      >
        {isCounting.value ? `${countdown.value}s` : '获取验证码'}
      </button>
    )

    return () => (
      <div class="login-wraps">
        {/* 头部背景 + logo */}
        <div class="head-box">
          <img src={siteConfigStore.siteLogo || appIcon} alt="logo" class="rounded-md" />
        </div>

        {/* Tab 容器 */}
        <div class="teab">
          <Tabs
            v-model:active={activeTab.value}
            lineWidth="70px"
            lineHeight="4px"
            titleInactiveColor="#999"
            titleActiveColor="#07c160"
            onChange={clearOnTabSwitch}
          >
            {{
              default: () => (
                <>
                  <Tab title="密码登录" name={1} titleClass="titlecalss">
                    <CellGroup class="register-body" border={false}>
                      {renderPhoneRow()}
                      {renderCaptchaRow()}
                      <div class="tbox">
                        <div class="icons">
                          <img src={iconShield} alt="lock" />
                        </div>
                        <div class="tright">
                          <div class="tips">密码</div>
                          <Field
                            v-model={password.value}
                            maxlength={32}
                            type="password"
                            placeholder="输入您的密码锁"
                          />
                        </div>
                      </div>
                    </CellGroup>
                  </Tab>
                  <Tab title="验证码登录" name={2} titleClass="titlecalss">
                    <CellGroup class="register-body" border={false}>
                      {renderPhoneRow()}
                      {renderCaptchaRow()}
                      <div class="tbox">
                        <div class="icons">
                          <img src={iconShield} alt="code" />
                        </div>
                        <div class="tright">
                          <div class="tips">验证码</div>
                          <Field v-model={code.value} maxlength={6} placeholder="输入短信验证码">
                            {{ 'right-icon': renderSendCodeButton }}
                          </Field>
                        </div>
                      </div>
                    </CellGroup>
                  </Tab>
                </>
              ),
              'nav-right': () => (
                <button type="button" class="closes btn-reset" aria-label="关闭" onClick={() => goBack()}>
                  <Icon icon="mdi:close" width="22" height="22" class="text-[#969799]" />
                </button>
              ),
            }}
          </Tabs>
        </div>

        {/* 登录按钮 */}
        <div class="footerbt">
          <VanButton loading={loginLoading.value} onClick={handleSubmit}>
            登录
          </VanButton>
        </div>

        {/* 立即注册 / 忘记密码 */}
        <div class="forgetpwd">
          {/* 样式挂在 `.forgetpwd span` 上：span 保留，链接包在外面（flex 让 span 仍是弹性项） */}
          <RouterLink class="btn-reset flex" to="/register">
            <span>立即注册</span>
          </RouterLink>
          <RouterLink class="btn-reset flex" to="/forgot-password">
            <span>忘记密码</span>
          </RouterLink>
        </div>
      </div>
    )
  },
})
